/*
  Последний шаг мастера «Новый лицевой счёт» (20.10.2026): что внесено по счёту.

  Это итог внесённого, а не проверка готовности к судебному пакету: серверный чек-лист
  готовности считает весь портфель, а не один счёт, и показывать здесь «не хватает
  такого-то» значило бы придумывать правила на клиенте. Недостающее для пакета сервер
  называет словами при сборке на вкладке «Суд» (строка реестра компромиссов).
*/

import { useCallback } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client.js'
import { Loading, Notice } from '../Common.jsx'
import useEntryList from './useEntryList.js'

// Число записей в списке берётся у сервера: страница из одной строки (`limit=1`) несёт
// общее `total`, и сами строки в браузер не едут (21.10.2026, серверная пагинация).
// Состав должников - небольшой список без страниц: его длина и есть число.
const ONE_ROW = { limit: 1, offset: 0 }

function count(state, one, few, many) {
  if (state.error) return 'не удалось загрузить'
  if (state.rows === null) return '…'
  const n = state.rows
  const tail = n % 10
  const word = n % 100 >= 11 && n % 100 <= 14 ? many : tail === 1 ? one : tail >= 2 && tail <= 4 ? few : many
  return `${n} ${word}`
}

export default function WizardSummary({ accountId, number, onAnother }) {
  const people = useEntryList(
    useCallback(() => api.participants(accountId).then((list) => list.length), [accountId]),
  )
  const charges = useEntryList(
    useCallback(() => api.charges(accountId, ONE_ROW).then((page) => page.total), [accountId]),
  )
  const payments = useEntryList(
    useCallback(() => api.payments(accountId, ONE_ROW).then((page) => page.total), [accountId]),
  )
  const residents = useEntryList(
    useCallback(() => api.residents(accountId, ONE_ROW).then((page) => page.total), [accountId]),
  )
  const loading = [people, charges, payments, residents].some(
    (state) => state.rows === null && !state.error,
  )

  return (
    <section className="section card">
      <h2 className="section__title">Шаг 6. Готово: счёт № {number}</h2>
      <Notice error={people.error ?? charges.error ?? payments.error ?? residents.error} />
      {loading ? <Loading what="Собираем итог" /> : null}
      <table className="table table--pairs">
        <tbody>
          <tr>
            <td className="muted">Должники</td>
            <td>{count(people, 'человек', 'человека', 'человек')}</td>
          </tr>
          <tr>
            <td className="muted">Начисления</td>
            <td>{count(charges, 'строка', 'строки', 'строк')}</td>
          </tr>
          <tr>
            <td className="muted">Оплаты</td>
            <td>{count(payments, 'запись', 'записи', 'записей')}</td>
          </tr>
          <tr>
            <td className="muted">Проживающие</td>
            <td>{count(residents, 'человек', 'человека', 'человек')}</td>
          </tr>
        </tbody>
      </table>
      <div className="muted">
        Чего не хватает для судебного пакета (протокол собрания по дому, идентификаторы
        должника), Арбитр ЖКХ скажет при сборке пакета на вкладке «Суд» карточки счёта.
      </div>
      <div className="row row--tight">
        <Link className="button" to={`/accounts/${accountId}`}>
          Открыть карточку счёта
        </Link>
        <button className="button button--quiet" type="button" onClick={onAnother}>
          Завести ещё один счёт
        </button>
      </div>
    </section>
  )
}
